import { pipe, Option } from 'effect';
import type { UseQueryOptions, WithRequired } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { companyCapabilitiesQuery } from '@/domain/companies/queries/capabilities';
import { erpConnectionsQuery } from '@/domain/erpIntegration/queries/connections';

/**
 * One query a route waits for. Data and key are `any`: the guard reads only whether the query succeeded, and a
 * concrete factory fits neither `unknown` nor `QueryKey` — both sit in callback parameters of the options
 * (`select`, `queryFn`, `enabled`).
 */
export type RouteWaitQuery = WithRequired<UseQueryOptions<any, AxiosError, any, any>, 'queryKey'>;

/**
 * The queries a route waits for, read from the Redux state like a selector. Take a company from
 * `currentCompanyIdSelector`, never from the URL: a request for a company the scope has not reached yet carries the
 * previous company's CompanyId header and is refused.
 * List only independent queries that always run: a query that needs another's data reads it in its own `queryFn`
 * (`queryClient.ensureQueryData`), and a disabled query without data stays `loading`, so the route would wait forever.
 */
export type RouteWaitQueries = (state: unknown) => RouteWaitQuery[];

const companyCapabilitiesInScope = (state: unknown): RouteWaitQuery[] =>
  pipe(Option.fromNullable(currentCompanyIdSelector(state)), Option.map(companyCapabilitiesQuery), Option.toArray);

const erpConnectionsInScope = (state: unknown): RouteWaitQuery[] =>
  pipe(Option.fromNullable(currentCompanyIdSelector(state)), Option.map(erpConnectionsQuery), Option.toArray);

// The document page reads the company features from the store, which cannot tell they are not loaded yet, and its
// publish, force-sync and More actions are decided by the connected ERP.
export const waitDocumentRouteQueries: RouteWaitQueries = (state) => [
  ...companyCapabilitiesInScope(state),
  ...erpConnectionsInScope(state),
];

// Mobile approvals read the company features from the store, which cannot tell they are not loaded yet.
export const waitDocumentMobileApprovalsRouteQueries: RouteWaitQueries = companyCapabilitiesInScope;
